import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LicenceAssignDialogComponent } from '../../Dialogs/licence-assign-dialog/licence-assign-dialog.component';
import { AdjustLicencesDialogComponent } from '../../../../Organizations/Components/Dialogs/adjust-licences-dialog/adjust-licences-dialog.component';
import { ResumeLicencesDialogComponent } from '../../../../Organizations/Components/Dialogs/resume-licences-dialog/resume-licences-dialog.component';
import { AvReasonDialogComponent } from '../../../../Shared/Components/av-reason-dialog/av-reason-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { AvOrganizationPickerComponent } from '../../../../Shared/Components/av-organization-picker/av-organization-picker.component';
import { LicencePortfolioFacade } from '../../../../../Facades/Licences/licence-portfolio.facade';
import { DATE_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  LICENCE_CANCEL_CONFIRM_LABEL,
  LICENCE_CANCEL_CONFIRM_TITLE,
  LICENCE_CANCEL_CONSEQUENCE,
  LICENCE_COMPONENT_OPTIONS,
  LICENCE_HOLDER_OPTIONS,
  LICENCE_RESUME_CONFIRM_LABEL,
  LICENCE_RESUME_CONFIRM_TITLE,
  LICENCE_RESUME_CONSEQUENCE,
  LICENCE_STATE_OPTIONS,
  LICENCE_SUBJECT_KEY,
  RELEASE_CONFIRM_LABEL,
  RELEASE_CONFIRM_TITLE,
  RELEASE_CONSEQUENCE,
} from '../../../../../Consts/licences.const';
import {
  LICENCE_HOLDER_LABEL,
  LICENCE_STATE_LABEL,
  LicenceState,
} from '../../../../../Enums/licence-state.enum';
import { PRICE_COMPONENT_LABEL, PriceComponentType } from '../../../../../Enums/price-component-type.enum';
import { Licence } from '../../../../../Models/backoffice/licence.model';
import {
  AdjustLicencesDialogResult,
  ResumeLicencesDialogResult,
} from '../../../../Organizations/Interfaces/licence-dialog.interface';
import { LicenceFilters } from '../../../../Organizations/Interfaces/licence-filters.interface';
import { ReasonDialogResult } from '../../../../Shared/Interfaces/reason-dialog.interface';
import { LicenceAssignDialogResult } from '../../../Interfaces/licence-assign-dialog.interface';

@Component({
  selector: 'app-licence-portfolio',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
    AvOrganizationPickerComponent,
  ],
  templateUrl: './licence-portfolio.component.html',
  styleUrl: './licence-portfolio.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class LicencePortfolioComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);
  private readonly _translate = inject(TranslateService);

  readonly facade = inject(LicencePortfolioFacade);

  readonly dateFormat = DATE_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly componentOptions = LICENCE_COMPONENT_OPTIONS;
  readonly stateOptions = LICENCE_STATE_OPTIONS;
  readonly holderOptions = LICENCE_HOLDER_OPTIONS;
  readonly stateLabelKeys = LICENCE_STATE_LABEL;
  readonly holderLabelKeys = LICENCE_HOLDER_LABEL;
  readonly componentLabelKeys = PRICE_COMPONENT_LABEL;
  readonly halfStateValue = LicenceState.HALF_STATE;

  private readonly _query = toSignal(this._route.queryParamMap);

  readonly isEmpty = computed<boolean>(
    () => this.facade.state() === 'ready' && this.facade.licences().length === 0,
  );

  constructor() {
    this._registerOrganizationQueryParam();
  }

  componentLabelKey(componentType: string): string {
    return this.componentLabelKeys[componentType as PriceComponentType] ?? componentType;
  }

  selectOrganization(organizationId: string): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { organization: organizationId || null },
      queryParamsHandling: 'merge',
    });
  }

  changeFilter(field: keyof LicenceFilters, value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), [field]: value });
  }

  showHalfState(): void {
    this.changeFilter('state', this.halfStateValue);
  }

  clearFilters(): void {
    this.facade.clearFilters();
  }

  goToPage(page: number): void {
    this.facade.goToPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  reload(): void {
    this.facade.reload();
  }

  openAdjust(): void {
    const reference = this._dialog.open(AdjustLicencesDialogComponent, {
      data: { components: this.facade.components() },
    });

    reference
      .afterClosed()
      .subscribe((result?: AdjustLicencesDialogResult) => this._adjust(result));
  }

  openResumeQuantity(): void {
    const reference = this._dialog.open(ResumeLicencesDialogComponent, {
      data: { components: this.facade.components() },
    });

    reference
      .afterClosed()
      .subscribe((result?: ResumeLicencesDialogResult) => this._resumeQuantity(result));
  }

  openAssign(licence: Licence): void {
    const reference = this._dialog.open(LicenceAssignDialogComponent, {
      data: {
        organizationName: this.facade.organizationName(),
        licenceLabel: this._subjectLabel(licence, licence.id),
        users: this.facade.users(),
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: LicenceAssignDialogResult) => this._assign(licence.id, result));
  }

  openRelease(licence: Licence): void {
    const reference = this._dialog.open(AvReasonDialogComponent, {
      data: {
        title: RELEASE_CONFIRM_TITLE,
        consequence: RELEASE_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        subjectLabel: this._subjectLabel(licence, licence.holderDisplay),
        confirmLabel: RELEASE_CONFIRM_LABEL,
        isDangerous: true,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: ReasonDialogResult) => this._release(licence.id, result));
  }

  openCancel(licence: Licence): void {
    const reference = this._dialog.open(AvReasonDialogComponent, {
      data: {
        title: LICENCE_CANCEL_CONFIRM_TITLE,
        consequence: LICENCE_CANCEL_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        subjectLabel: this._subjectLabel(licence, licence.id),
        confirmLabel: LICENCE_CANCEL_CONFIRM_LABEL,
        isDangerous: true,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: ReasonDialogResult) => this._cancelOne(licence.id, result));
  }

  openResume(licence: Licence): void {
    const reference = this._dialog.open(AvReasonDialogComponent, {
      data: {
        title: LICENCE_RESUME_CONFIRM_TITLE,
        consequence: LICENCE_RESUME_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        subjectLabel: this._subjectLabel(licence, licence.id),
        confirmLabel: LICENCE_RESUME_CONFIRM_LABEL,
        isDangerous: false,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: ReasonDialogResult) => this._resumeOne(licence.id, result));
  }

  private _subjectLabel(licence: Licence, reference: string): string {
    return this._translate.instant(LICENCE_SUBJECT_KEY, {
      component: this._translate.instant(licence.componentLabelKey),
      reference,
    });
  }

  private _registerOrganizationQueryParam(): void {
    effect(() => {
      this.facade.selectOrganization(this._query()?.get('organization') ?? '');
    });
  }

  private _adjust(result?: AdjustLicencesDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.adjust({
      component_type: result.componentType,
      delta: result.delta,
      reason: result.reason,
    });
  }

  private _resumeQuantity(result?: ResumeLicencesDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.resumeQuantity({
      component_type: result.componentType,
      quantity: result.quantity,
      reason: result.reason,
    });
  }

  private _assign(licenceId: string, result?: LicenceAssignDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.assign(licenceId, result.userId, result.reason);
  }

  private _release(licenceId: string, result?: ReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.release(licenceId, result.reason);
  }

  private _cancelOne(licenceId: string, result?: ReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.cancelOne(licenceId, result.reason);
  }

  private _resumeOne(licenceId: string, result?: ReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.resumeOne(licenceId, result.reason);
  }
}
